/** @format */
"use client";

import { ProductInterface } from "@/types/product";
import { useEffect, useState } from "react";
import {
  Star,
  Plus,
  Minus,
  Truck,
  Shield,
  RotateCcw,
  Gift,
  Award,
  Clock,
  ChevronRight,
  Package,
  MessageSquare,
  CheckCircle2,
  ShoppingCart,
  Zap,
} from "lucide-react";
import { FormatUtils } from "@/utils/format";
import EditorClientWrapper from "@/components/editor/editor-wrapper";
import { discountTypeEnum } from "@/types/promotion";
import ProductSuggess from "./components/ProductSuggest";
import { useAddToCart } from "@/hooks/use-addToCart";
import SoldInfo from "@/components/ui/soldInfo";
import ProductImageGallery from "./components/ProductImageGallery";

interface propsProductClientPC {
  product: ProductInterface;
}

/* ─────────────────────────────────────────
   SUB-COMPONENTS
───────────────────────────────────────── */

const RatingStars = ({ rating }: { rating: number }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className="w-3.5 h-3.5"
        style={{
          fill: i < rating ? "var(--brand-primary)" : "transparent",
          color: i < rating ? "var(--brand-primary)" : "var(--border)",
        }}
      />
    ))}
  </div>
);

const InfoBadge = ({ children }: { children: React.ReactNode }) => (
  <span
    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border"
    style={{
      backgroundColor: "rgba(200,169,110,0.08)",
      borderColor: "rgba(200,169,110,0.25)",
      color: "var(--brand-primary)",
      fontFamily: "var(--font-montserrat)",
    }}>
    {children}
  </span>
);

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */

export const ProductClient = ({ product }: propsProductClientPC) => {
  const TABS = [
    { key: "description", label: "Mô tả sản phẩm", icon: Package },
    { key: "reviews", label: "Đánh giá", icon: MessageSquare },
    { key: "shipping", label: "Vận chuyển", icon: Truck },
  ];

  const FEATURES = [
    { icon: Truck, title: "Miễn phí vận chuyển", desc: "Đơn trên 5.000.000đ" },
    { icon: Shield, title: "Thanh toán bảo mật", desc: "100% được bảo vệ" },
    { icon: RotateCcw, title: "Đổi trả dễ dàng", desc: "15 ngày đổi trả" },
  ];

  const [selectedColor, setSelectedColor] = useState(
    product.colors?.[0] || null,
  );
  const [selectedSize, setSelectedSize] = useState(product.sizes?.[0] || null);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");
  const [isMount, setIsMount] = useState(false);
  const cart = useAddToCart();
  const promotion = product.promotionProducts[0];

  useEffect(() => {
    setIsMount(true);
  }, []);

  const discountPercentage = (() => {
    if (promotion) {
      const base = product.price;
      const promo =
        promotion.discountType === discountTypeEnum.PERCENT
          ? base * (1 - promotion.discount / 100)
          : base - promotion.discount;
      return Math.round(((base - promo) / base) * 100);
    }
    if (product.originalPrice && product.originalPrice > product.price) {
      return Math.round(
        ((product.originalPrice - product.price) / product.originalPrice) * 100,
      );
    }
    return 0;
  })();

  const showLineThroughPrice = promotion
    ? product.price
    : product.originalPrice &&
        product.originalPrice > 0 &&
        product.originalPrice > product.price
      ? product.originalPrice
      : null;

  const getDiscountedPrice = () => {
    const promo = product.promotionProducts[0];
    if (!promo) return product.price;
    return promo.discountType === discountTypeEnum.PERCENT
      ? product.price * (1 - promo.discount / 100)
      : product.price - promo.discount;
  };

  const handleQuantityChange = (type: "increase" | "decrease") => {
    if (type === "increase" && quantity < product.stock)
      setQuantity((q) => q + 1);
    if (type === "decrease" && quantity > 1) setQuantity((q) => q - 1);
  };

  const handleAddToCart = async (e: React.MouseEvent, isCheckout: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    await cart.addToCart({ product, isCheckout });
  };

  if (!isMount) return null;

  const discountedPrice = getDiscountedPrice();
  const isOutOfStock = product.stock <= 0;
  const isLowStock = product.stock > 0 && product.stock < 10;

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: "var(--background)",
        color: "var(--foreground)",
        fontFamily: "var(--font-roboto)",
      }}>
      {/* ── BREADCRUMB ── */}
      <div
        className="sticky top-0 z-20 border-b"
        style={{
          backgroundColor: "rgba(13,13,13,0.85)",
          borderColor: "var(--border)",
          backdropFilter: "blur(12px)",
        }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav
            className="flex items-center gap-1.5 text-xs"
            aria-label="Breadcrumb">
            {["Trang chủ", "Sản phẩm"].map((crumb) => (
              <span key={crumb} className="flex items-center gap-1.5">
                <span
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                  style={{ color: "var(--muted-foreground)" }}>
                  {crumb}
                </span>
                <ChevronRight
                  className="w-3 h-3"
                  style={{ color: "var(--border)" }}
                />
              </span>
            ))}
            <span
              className="font-semibold truncate max-w-[200px] sm:max-w-xs"
              style={{
                color: "var(--brand-primary)",
                fontFamily: "var(--font-montserrat)",
              }}
              title={product.name}>
              {product.name}
            </span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* ── PRODUCT GRID ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mb-12">
          {/* LEFT: Images */}
          <div className="lg:sticky lg:top-20 lg:self-start">
            <ProductImageGallery product={product} />
          </div>

          {/* RIGHT: Info */}
          <div className="space-y-5">
            {/* ── NAME & META ── */}
            <div>
              {/* Tags row */}
              <div className="flex items-center gap-2 flex-wrap mb-3">
                {isOutOfStock ? (
                  <InfoBadge>
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                    Hết hàng
                  </InfoBadge>
                ) : (
                  <InfoBadge>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Còn hàng
                  </InfoBadge>
                )}
                {discountPercentage > 0 && (
                  <span
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-black"
                    style={{
                      backgroundColor: "rgba(251,33,80,0.12)",
                      color: "#fb2150",
                      fontFamily: "var(--font-montserrat)",
                      border: "1px solid rgba(251,33,80,0.25)",
                    }}>
                    -{discountPercentage}% OFF
                  </span>
                )}
                {isLowStock && (
                  <span
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
                    style={{
                      backgroundColor: "rgba(245,158,11,0.1)",
                      color: "#f59e0b",
                      border: "1px solid rgba(245,158,11,0.25)",
                      fontFamily: "var(--font-montserrat)",
                    }}>
                    <Clock className="w-3 h-3" />
                    Sắp hết hàng
                  </span>
                )}
              </div>

              {/* Product name */}
              <h1
                className="text-xl sm:text-2xl lg:text-3xl font-black leading-snug mb-3"
                style={{
                  fontFamily: "var(--font-montserrat)",
                  color: "var(--foreground)",
                }}>
                {product.name}
              </h1>

              {/* Rating + sold + SKU row */}
              <div
                className="flex items-center gap-3 flex-wrap pb-4 border-b"
                style={{ borderColor: "var(--border)" }}>
                <RatingStars rating={5} />
                <div
                  style={{ color: "var(--muted-foreground)" }}
                  className="text-xs">
                  <SoldInfo sold={product.saleCount ?? 10000} />
                </div>
                <div
                  className="h-3 w-px"
                  style={{ backgroundColor: "var(--border)" }}
                />
                <span
                  className="text-xs"
                  style={{ color: "var(--muted-foreground)" }}>
                  SKU:{" "}
                  <span
                    style={{
                      color: "var(--foreground)",
                      fontFamily: "var(--font-montserrat)",
                    }}>
                    {product.sku}
                  </span>
                </span>
              </div>
            </div>

            {/* ── PRICE BLOCK ── */}
            <div
              className="rounded-2xl p-5 border"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "rgba(200,169,110,0.25)",
                boxShadow: "0 0 0 1px rgba(200,169,110,0.06)",
              }}>
              {/* Gold top bar */}
              <div
                className="h-0.5 w-8 rounded-full mb-4"
                style={{ backgroundColor: "var(--brand-primary)" }}
              />

              <div className="flex items-end gap-3 mb-1">
                <span
                  className="text-3xl sm:text-4xl font-black"
                  style={{
                    fontFamily: "var(--font-montserrat)",
                    color: "var(--foreground)",
                  }}>
                  {discountedPrice === 0
                    ? "Liên hệ"
                    : FormatUtils.formatPriceVND(discountedPrice)}
                </span>

                {showLineThroughPrice && showLineThroughPrice > 0 && (
                  <span
                    className="text-base sm:text-lg line-through pb-1"
                    style={{ color: "var(--muted-foreground)" }}>
                    {FormatUtils.formatPriceVND(showLineThroughPrice)}
                  </span>
                )}
              </div>

              {product.price > 0 && (
                <p
                  className="text-xs"
                  style={{ color: "var(--muted-foreground)" }}>
                  Kho còn:{" "}
                  <span
                    className="font-semibold"
                    style={{ color: isLowStock ? "#f59e0b" : "#22c55e" }}>
                    {product.stock} sản phẩm
                  </span>
                </p>
              )}
            </div>

            {/* ── VARIANTS ── */}
            {(product.colors?.length > 0 || product.sizes?.length > 0) &&
              product.price > 0 && (
                <div
                  className="rounded-2xl p-5 border space-y-5"
                  style={{
                    backgroundColor: "var(--card)",
                    borderColor: "var(--border)",
                  }}>
                  {/* Color */}
                  {product.colors?.length > 0 && (
                    <div>
                      <p
                        className="text-xs font-bold uppercase tracking-widest mb-3"
                        style={{
                          color: "var(--muted-foreground)",
                          fontFamily: "var(--font-montserrat)",
                        }}>
                        Màu sắc
                        {selectedColor && (
                          <span style={{ color: "var(--brand-primary)" }}>
                            {" "}
                            — {selectedColor.name}
                          </span>
                        )}
                      </p>
                      <div className="flex flex-wrap gap-2.5">
                        {product.colors.map((color, i) => (
                          <button
                            key={i}
                            onClick={() => setSelectedColor(color)}
                            title={color.name}
                            className="w-9 h-9 rounded-full transition-all duration-150"
                            style={{
                              backgroundColor: color.hex,
                              border:
                                selectedColor === color
                                  ? "2.5px solid var(--brand-primary)"
                                  : "2px solid var(--border)",
                              boxShadow:
                                selectedColor === color
                                  ? "0 0 0 3px rgba(200,169,110,0.2)"
                                  : "none",
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Size */}
                  {product.sizes?.length > 0 && (
                    <div>
                      <p
                        className="text-xs font-bold uppercase tracking-widest mb-3"
                        style={{
                          color: "var(--muted-foreground)",
                          fontFamily: "var(--font-montserrat)",
                        }}>
                        Kích thước
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {product.sizes.map((size, i) => (
                          <button
                            key={i}
                            onClick={() => setSelectedSize(size)}
                            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-150"
                            style={{
                              fontFamily: "var(--font-montserrat)",
                              backgroundColor:
                                selectedSize === size
                                  ? "var(--brand-primary)"
                                  : "var(--background)",
                              color:
                                selectedSize === size
                                  ? "var(--brand-primary-foreground)"
                                  : "var(--muted-foreground)",
                              border:
                                selectedSize === size
                                  ? "1.5px solid var(--brand-primary)"
                                  : "1.5px solid var(--border)",
                            }}>
                            {size.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

            {/* ── QUANTITY + CTA ── */}
            {product.price > 0 && (
              <div className="space-y-3">
                {/* Quantity */}
                <div className="flex items-center gap-3">
                  <p
                    className="text-xs font-bold uppercase tracking-widest"
                    style={{
                      color: "var(--muted-foreground)",
                      fontFamily: "var(--font-montserrat)",
                    }}>
                    Số lượng
                  </p>
                  <div
                    className="flex items-center rounded-xl overflow-hidden border"
                    style={{
                      borderColor: "var(--border)",
                      backgroundColor: "var(--card)",
                    }}>
                    <button
                      onClick={() => handleQuantityChange("decrease")}
                      disabled={quantity <= 1 || isOutOfStock}
                      className="w-10 h-10 flex items-center justify-center transition-colors hover:bg-white/5 disabled:opacity-30"
                      style={{ color: "var(--muted-foreground)" }}>
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span
                      className="w-12 text-center font-black text-sm"
                      style={{
                        fontFamily: "var(--font-montserrat)",
                        color: "var(--foreground)",
                      }}>
                      {quantity}
                    </span>
                    <button
                      onClick={() => handleQuantityChange("increase")}
                      disabled={quantity >= product.stock || isOutOfStock}
                      className="w-10 h-10 flex items-center justify-center transition-colors hover:bg-white/5 disabled:opacity-30"
                      style={{ color: "var(--muted-foreground)" }}>
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={(e) => handleAddToCart(e, false)}
                    disabled={isOutOfStock}
                    className="flex-1 flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-sm border transition-all duration-200 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      fontFamily: "var(--font-montserrat)",
                      borderColor: "rgba(200,169,110,0.4)",
                      color: "var(--brand-primary)",
                      backgroundColor: "rgba(200,169,110,0.06)",
                    }}>
                    <ShoppingCart className="w-4 h-4" />
                    Thêm vào giỏ
                  </button>

                  <button
                    onClick={(e) => handleAddToCart(e, true)}
                    disabled={isOutOfStock}
                    className="flex-1 flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-sm transition-all duration-200 hover:opacity-90 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      fontFamily: "var(--font-montserrat)",
                      backgroundColor: "var(--brand-primary)",
                      color: "var(--brand-primary-foreground)",
                      boxShadow: "0 4px 20px rgba(200,169,110,0.3)",
                    }}>
                    <Zap className="w-4 h-4" />
                    Mua ngay
                  </button>
                </div>
              </div>
            )}

            {/* ── GIFT ── */}
            {product.giftProducts!.length > 0 && (
              <div
                className="rounded-2xl p-5 border flex items-start gap-4"
                style={{
                  backgroundColor: "rgba(200,169,110,0.06)",
                  borderColor: "rgba(200,169,110,0.25)",
                }}>
                <div
                  className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{
                    backgroundColor: "rgba(200,169,110,0.12)",
                    border: "1.5px solid rgba(200,169,110,0.3)",
                  }}>
                  <Gift
                    className="w-4 h-4"
                    style={{ color: "var(--brand-primary)" }}
                  />
                </div>
                <div>
                  <p
                    className="text-xs font-bold uppercase tracking-widest mb-2"
                    style={{
                      color: "var(--brand-primary)",
                      fontFamily: "var(--font-montserrat)",
                    }}>
                    Quà tặng kèm theo
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {product.giftProducts!.map((g, i) => (
                      <span
                        key={i}
                        className="text-xs px-3 py-1.5 rounded-lg font-medium"
                        style={{
                          backgroundColor: "var(--card)",
                          color: "var(--foreground)",
                          border: "1px solid var(--border)",
                        }}>
                        🎁 {g.gift.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── FEATURES ── */}
            <div className="grid grid-cols-3 gap-3">
              {FEATURES.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="flex flex-col items-center text-center gap-2 p-4 rounded-2xl border"
                  style={{
                    backgroundColor: "var(--card)",
                    borderColor: "var(--border)",
                  }}>
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{
                      backgroundColor: "rgba(200,169,110,0.1)",
                      border: "1.5px solid rgba(200,169,110,0.2)",
                    }}>
                    <Icon
                      className="w-4 h-4"
                      style={{ color: "var(--brand-primary)" }}
                    />
                  </div>
                  <p
                    className="text-xs font-bold leading-snug"
                    style={{
                      fontFamily: "var(--font-montserrat)",
                      color: "var(--foreground)",
                    }}>
                    {title}
                  </p>
                  <p
                    className="text-xs"
                    style={{ color: "var(--muted-foreground)" }}>
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── TABS ── */}
        <div
          className="rounded-3xl border overflow-hidden mb-12"
          style={{
            backgroundColor: "var(--card)",
            borderColor: "var(--border)",
          }}>
          {/* Tab nav */}
          <div
            className="flex border-b"
            style={{ borderColor: "var(--border)" }}>
            {TABS.map(({ key, label, icon: Icon }) => {
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-1.5 py-4 px-3 text-xs sm:text-sm font-bold transition-all duration-150"
                  style={{
                    fontFamily: "var(--font-montserrat)",
                    color: isActive
                      ? "var(--brand-primary)"
                      : "var(--muted-foreground)",
                    borderBottom: isActive
                      ? "2px solid var(--brand-primary)"
                      : "2px solid transparent",
                    backgroundColor: isActive
                      ? "rgba(200,169,110,0.06)"
                      : "transparent",
                  }}>
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="p-6 sm:p-8 lg:p-10">
            {/* DESCRIPTION */}
            {activeTab === "description" && (
              <div>
                <h3
                  className="text-xl font-black mb-6"
                  style={{
                    fontFamily: "var(--font-montserrat)",
                    color: "var(--foreground)",
                  }}>
                  Mô tả chi tiết sản phẩm
                </h3>
                <div
                  style={{ color: "var(--muted-foreground)" }}
                  className="leading-relaxed text-sm sm:text-base">
                  <EditorClientWrapper jsonString={product.description} />
                </div>
              </div>
            )}

            {/* REVIEWS */}
            {activeTab === "reviews" && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3
                    className="text-xl font-black"
                    style={{
                      fontFamily: "var(--font-montserrat)",
                      color: "var(--foreground)",
                    }}>
                    Đánh giá từ khách hàng
                  </h3>
                  {/* Overall rating pill */}
                  <div
                    className="flex items-center gap-2 px-4 py-2 rounded-xl border"
                    style={{
                      backgroundColor: "rgba(200,169,110,0.08)",
                      borderColor: "rgba(200,169,110,0.25)",
                    }}>
                    <Star
                      className="w-4 h-4 fill-current"
                      style={{ color: "var(--brand-primary)" }}
                    />
                    <span
                      className="text-sm font-black"
                      style={{
                        fontFamily: "var(--font-montserrat)",
                        color: "var(--foreground)",
                      }}>
                      5.0
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  {product.fakeComments.map((comment) => (
                    <div
                      key={comment.id}
                      className="rounded-2xl p-5 border"
                      style={{
                        backgroundColor: "var(--background)",
                        borderColor: "var(--border)",
                      }}>
                      <div className="flex items-start gap-4">
                        {/* Avatar */}
                        <div
                          className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-black"
                          style={{
                            backgroundColor: "rgba(200,169,110,0.12)",
                            color: "var(--brand-primary)",
                            fontFamily: "var(--font-montserrat)",
                            border: "1.5px solid rgba(200,169,110,0.25)",
                          }}>
                          {comment.authorName.charAt(0)}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <span
                              className="font-bold text-sm truncate"
                              style={{
                                fontFamily: "var(--font-montserrat)",
                                color: "var(--foreground)",
                              }}>
                              {comment.authorName}
                            </span>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <RatingStars rating={comment.ratingCount} />
                              <span
                                className="text-xs"
                                style={{ color: "var(--muted-foreground)" }}>
                                {comment.ratingCount}.0
                              </span>
                            </div>
                          </div>
                          <p
                            className="text-sm leading-relaxed"
                            style={{ color: "var(--muted-foreground)" }}>
                            {comment.content}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SHIPPING */}
            {activeTab === "shipping" && (
              <div>
                <h3
                  className="text-xl font-black mb-6"
                  style={{
                    fontFamily: "var(--font-montserrat)",
                    color: "var(--foreground)",
                  }}>
                  Thông tin vận chuyển &amp; bảo hành
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    {
                      icon: Truck,
                      title: "Giao Hàng Chính Hãng",
                      desc: "Sản phẩm chính hãng 100%",
                    },
                    {
                      icon: RotateCcw,
                      title: "Đổi Trả Miễn Phí",
                      desc: "Trong vòng 7 ngày",
                    },
                    {
                      icon: Truck,
                      title: "Giao Hàng Nhanh",
                      desc: "Giao hàng toàn quốc",
                    },
                    {
                      icon: Shield,
                      title: "Bảo Hành Chính Hãng",
                      desc: "Bảo hành 12–36 tháng",
                    },
                    {
                      icon: Award,
                      title: "Chất Lượng Tiêu Chuẩn",
                      desc: "Kiểm tra nghiêm ngặt",
                    },
                    {
                      icon: CheckCircle2,
                      title: "Cam Kết Chất Lượng",
                      desc: "Hoàn tiền nếu lỗi nhà sản xuất",
                    },
                  ].map(({ icon: Icon, title, desc }) => (
                    <div
                      key={title}
                      className="flex items-start gap-4 p-5 rounded-2xl border transition-all duration-150"
                      style={{
                        backgroundColor: "var(--background)",
                        borderColor: "var(--border)",
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.borderColor =
                          "rgba(200,169,110,0.4)";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.borderColor =
                          "var(--border)";
                      }}>
                      <div
                        className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{
                          backgroundColor: "rgba(200,169,110,0.1)",
                          border: "1.5px solid rgba(200,169,110,0.2)",
                        }}>
                        <Icon
                          className="w-4 h-4"
                          style={{ color: "var(--brand-primary)" }}
                        />
                      </div>
                      <div>
                        <p
                          className="font-bold text-sm mb-0.5"
                          style={{
                            fontFamily: "var(--font-montserrat)",
                            color: "var(--foreground)",
                          }}>
                          {title}
                        </p>
                        <p
                          className="text-xs leading-relaxed"
                          style={{ color: "var(--muted-foreground)" }}>
                          {desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── SUGGESTED ── */}
        <ProductSuggess product={product} />
      </div>
    </div>
  );
};
